import React from 'react';
import clsx from 'clsx';
import {useTranslation} from 'react-i18next';
import { Button } from 'reactstrap';

import { useEditorState, useEditorActions  } from './editor-context'
import useWhoami from 'hooks/useWhoami'
import bootbox from 'components/Modals/Bootbox';

function SimpleBanner({ className, title, background = 'warning', textColor = 'dark', message, style }) {
  return (
    <div className={clsx(`bg-${background} text-${textColor} p-3 position-absolute w-100`, className)} style={{top: 40}}>
      <span>{title && <b>{title}</b>} {message}</span>
    </div>
  )
}

function Banner({ className, icon, title, style, buttons, background = 'warning', textColor = 'dark', onClick, children }) {
  return (
    <div className={clsx(`d-flex justify-content-between align-items-center bg-${background} text-${textColor} p-3 position-absolute w-100`, className)} style={style}>
      <div>
      { icon && <i className={`fas fa-${icon}`}></i> } {title}
      </div>
      
      { buttons && 
        <div className="pull-right">
          {
            buttons.map(button => (
              <Button size="sm" color="link" key={button.id} className={button.className} onClick={button.onClick}>
                { button.icon && <i className={`fas fa-${button.icon}`}></i> }{' '}
                {button.text}
              </Button>
            ))
          }
        </div> 
      }
      {children}
    </div>
  )
}

export default function BannerContainer() {
  const { t } = useTranslation()
  const { document: doc, isConfigOk, isPreviewOnly, isEditable, mode, documentType } = useEditorState()
  const { saveDocument, migrateDocument } = useEditorActions()
  const { whoami } = useWhoami(doc)

  const isParticipantsOk =  doc.participants?.length >= 1 && doc.participants.every((participant) => participant.label)
  const isConfig = isParticipantsOk && isConfigOk;
  const collaboration = doc.collaboration;
  const participantWithNegotiationTurn = doc.participants?.find(p => p.status === 'NEGOTIATION_TAKEN') ?? {}
  

  const isBeingApproved = whoami.someoneGaveVobo();
  const everyoneApproved = whoami.everyoneApproved();
  const someoneHasNegotiationTurn = whoami.someoneHasNegotiationTurn();
  const  isBeingNegotiated = whoami.isBeingNegotiated();
  const collaborator = whoami.getRevisionCollaborator();
  const isOwner = whoami.isOwner();

  const handleMigrate = async () => {
    if (!isOwner) return ;
      const confirm = await bootbox.confirm({ 
          textT: `
          <i className="fas fa-exclamation-triangle text-warning fa-2x"></i> <strong>¿Estás seguro que quieres convertir este documento? </strong>
          <br /><br /> 
          Si decides hacer la conversión revisa tu documento ya que el formato pudo verse afectado, si necesitas ayuda escribe a <a href="mailto:soporte@trato.io" >soporte@trato.io</a>`
      });

      if (confirm) {
          migrateDocument()
      }
  }
  
  return (
    <>
      {doc.status === 'PRE_APPROVED' && 
        <SimpleBanner title={t('editor.canvas.document_pre_approved')} message={t('editor.canvas.changes_must_be_approved_again')} />
      }
      {doc.status !== 'PRE_APPROVED' && (isBeingApproved || everyoneApproved) &&  mode === 'contract' &&
        <SimpleBanner title={t('editor.canvas.document_is_being_approved')} message={t('editor.canvas.changes_must_be_approved_again')} />
      }
      {doc.status === 'ACCEPTED' && 
        <SimpleBanner background="success" textColor="light" title={t('editor.canvas.contract_accepted')}/>
      }
      {doc.status === 'REJECTED' && 
        <SimpleBanner background="danger" textColor="light" title={t('editor.canvas.contract_rejected')}/>
      }
      {!doc.isMigrated && documentType === 'trato' &&
        <Banner 
          background="warning" 
          textColor="light" 
          style={{top: 40}} 
          title={t(`Este documento se encuentra en una versión anterior de TRATO${isOwner ? ', si deseas convertirlo presiona "Convertir documento"' : ''}`)}
          buttons={isOwner ? [{ id: 1, className: "text-light", text: t('Convertir documento'), onClick: handleMigrate }] : undefined}
        />
      }
      {!isConfig && isEditable &&
        <Banner 
          icon="cog"
          title={t('editor.action.configure')}
          buttons={[{ id: 1, text: t('editor.action.save'), onClick: () => saveDocument() }]}
        />
      }
      {isBeingNegotiated  &&
        <Banner background="success" textColor="light" style={{top: 40}}>
          {someoneHasNegotiationTurn ? 
            <span>{participantWithNegotiationTurn.name} {participantWithNegotiationTurn.last_name} ha tomado el turno de la negociación. 
            {isOwner ? `Deberás esperar a que ${participantWithNegotiationTurn.name} ${participantWithNegotiationTurn.last_name} suba una nueva versión para editar el presente documento.`: null}
            </span>
          :
            <span>Ninguno de los participantes ha tomado el turno de la negociación. 
            {isOwner ? "Puedes todavía editar el presente documento." : null}
            </span>
          }
        </Banner>
      }

      {collaboration &&
        <Banner 
          icon="cog"
          title={t('editor.collaboration.revision_banner')}
          buttons={[{ 
            id: 1,
            className: 'm-r text-danger', 
            text: t('editor.collaboration.revision_banner_cancel') 
          },{ 
            id: 2,
            text: t('editor.collaboration.revision_send') 
          }]}
        />
      }

      {!!Object.keys(collaborator).length &&
        <Banner 
          icon="check"
          title={`${doc.user?.givenName} ${doc.user?.familyName} ${t('editor.collaboration.revision_requested')}`}
          buttons={[{ 
            id:1,
            icon: 'check',
            text: t('editor.collaboration.revision_send_reviewed') 
          },{ 
            id:2,
            className: 'm-l',
            icon: 'save',
            text: t('editor.collaboration.revision_temp_save') 
          }]}
        />
      }
      {isPreviewOnly && 
        <SimpleBanner message={t('contracts.negotiation.visualization_version')} />
      }
  </>
  )
}